"use client";

import { useLayoutEffect, type RefObject } from "react";
import gsap from "gsap";

const EXPO = "expo.out";

export function useHeroIntro(root: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.classList.remove("is-pending");
      el.classList.add("is-done");
      return;
    }

    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(el);
      const eyebrows = q('[data-intro="eyebrow"]');
      const titleMasks = q('[data-intro="title"] .rv-mask');
      const leadUnits = q('[data-intro="lead"] .rv-unit');
      const decos = q('[data-intro="title"] .hl-wash, [data-intro="title"] .hl-texture, [data-intro="title"] .hl-tick-l');
      const tickRight = q('[data-intro="title"] .hl-tick-r');
      const cta = q('[data-intro="cta"]');
      const chips = q('[data-intro="chip"]');

      let line = -1;
      let top = -Infinity;
      const lines = titleMasks.map((mask) => {
        const box = mask.getBoundingClientRect();
        if (box.top - top > box.height * 0.5) {
          line++;
          top = box.top;
        }
        return line;
      });
      const titleUnits = titleMasks.map((mask) => mask.firstElementChild as HTMLElement);
      const drop = (unit: HTMLElement) => (unit.parentElement?.getBoundingClientRect().height ?? 0) * 1.15;
      const hidden = "inset(-20% 100% -20% 0%)";
      const shown = "inset(-20% -20% -20% 0%)";

      gsap.set(eyebrows, { autoAlpha: 0, y: "1.5rem" });
      gsap.set(titleUnits, { y: (_: number, unit: HTMLElement) => drop(unit) });
      gsap.set(leadUnits, { y: (_: number, unit: HTMLElement) => drop(unit) });
      gsap.set([...decos, ...tickRight], { clipPath: hidden });
      gsap.set(cta, { autoAlpha: 0, y: "2rem" });
      gsap.set(chips, { autoAlpha: 0, scale: 0.96 });
      el.classList.remove("is-pending");

      const tl = gsap.timeline({
        onComplete: () => {
          el.classList.add("is-done");
          gsap.set([...eyebrows, ...titleUnits, ...leadUnits, ...decos, ...tickRight, ...cta, ...chips], {
            clearProps: "transform,opacity,visibility,clipPath",
          });
        },
      });
      tl.to(eyebrows, { autoAlpha: 1, duration: 0.6, ease: "power1.inOut" }, 0)
        .to(eyebrows, { y: 0, duration: 0.9, ease: EXPO }, 0)
        .to(titleUnits, { y: 0, duration: 0.9, ease: EXPO, delay: (i: number) => 0.18 + lines[i] * 0.12 }, 0)
        .to(decos, { clipPath: shown, duration: 0.9, ease: EXPO }, 0.12)
        .to(tickRight, { clipPath: shown, duration: 0.6, ease: EXPO }, 0.35)
        .to(leadUnits, { y: 0, duration: 0.9, ease: EXPO, delay: (i: number) => 0.66 + i * 0.012 }, 0)
        .to(cta, { autoAlpha: 1, y: 0, duration: 0.9, ease: "quart.out" }, 0.8)
        .to(chips, { autoAlpha: 1, duration: 0.6, ease: "power1.inOut", stagger: 0.14 }, 1)
        .to(chips, { scale: 1, duration: 0.9, ease: EXPO, stagger: 0.14 }, 1);
    }, el);

    return () => ctx.revert();
  }, [root]);
}
